import { useMutation, useQuery } from "@tanstack/react-query";
import rg4js from "raygun4js";
import { Col, Container } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import type { DocumentAddInterrogatoryRequest } from "@/briefpoint-client";
import Button from "@/components/Button";
import Loading from "@/components/Loading";
import PdfViewer from "@/components/PdfViewer";
import { toast } from "@/components/ui/toast";
import { useDocumentApi } from "@/hooks/useApi";
import { getDocumentOptions } from "@/lib/query-options";
import type { DocumentDetailClient } from "@/lib/types";
import { addUploadDate, normalizeError } from "@/lib/utils";
import InterrogatoriesList from "./InterrogatoriesList";

export default function UpdateInterrogatories() {
  const { documentId } = useParams<{ documentId: string }>();
  const { interrogatoryId } = useParams<{ interrogatoryId: string }>();
  const documentApi = useDocumentApi();

  const deleteInterrogatoryMutation = useMutation({
    mutationFn: (interrogatoryId: number) =>
      documentApi.documentDeleteInterrogatory({
        documentId: documentId,
        interrogatoryId: interrogatoryId,
      }),
    onError: (error) => {
      toast.error("Failed to delete interrogatory");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      documentQuery.refetch();
    },
  });

  const documentQuery = useQuery({
    ...getDocumentOptions(documentId, documentApi),
    enabled: !!documentId,
    select: (data) => addUploadDate(data) as DocumentDetailClient,
  });

  const updateInterrogatoryMutation = useMutation({
    mutationFn: ({
      number,
      text,
      interrogatoryId,
      pleadDocId,
    }: {
      number: number;
      text?: string;
      interrogatoryId: number;
      pleadDocId: string;
    }) =>
      documentApi.documentUpdateInterrogatory({
        documentId: pleadDocId || documentId,
        interrogatoryId: interrogatoryId,
        updateInterrogatory: { text, number },
      }),
    onError: (error) => {
      toast.error("Failed to update interrogatory");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      documentQuery.refetch();
    },
  });

  const addInterrogatoryMutation = useMutation({
    mutationFn: (params: DocumentAddInterrogatoryRequest) =>
      documentApi.documentAddInterrogatory({
        documentId: documentId,
        updateInterrogatory: params.updateInterrogatory,
      }),

    onError: (error) => {
      toast.error("Failed to add interrogatory");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      documentQuery.refetch();
    },
  });

  const history = useHistory();

  function onConfirm() {
    if (documentQuery.data?.id) {
      history.push(`/wizard/${documentQuery.data.id}`);
    }
  }

  return (
    <Loading className="mt-3" isLoading={!document}>
      <Container className="page-container">
        <div className="mb-4">
          <div className="d-flex">
            <Col xs="5">
              <div className="d-flex justify-content-between">
                <h2>Update Text</h2>
              </div>
              <div className="interrogatory-list-wrapper list-column">
                <InterrogatoriesList
                  interrogatoriesList={
                    documentQuery.data?.interrogatories || []
                  }
                  interrogatoryId={interrogatoryId}
                  onAdd={(index, text) =>
                    addInterrogatoryMutation.mutate({
                      documentId: documentId,
                      updateInterrogatory: {
                        number: index + 1,
                        text: text,
                      },
                    })
                  }
                  onEdit={(id, index, text) =>
                    updateInterrogatoryMutation.mutate({
                      number: index + 1,
                      text: text,
                      interrogatoryId: id,
                      pleadDocId: documentId,
                    })
                  }
                  onDelete={deleteInterrogatoryMutation.mutate}
                  documentSubType={documentQuery.data?.info?.documentSubType}
                  startingNumber={documentQuery.data?.info?.startingNumber || 1}
                />
              </div>
            </Col>
            <Col xs="7">
              <h2>Source Document</h2>
              <div>
                <PdfViewer documentId={documentId} height={800} width={365} />
              </div>
            </Col>
          </div>
        </div>
        <Button type="button" variant="default" onClick={onConfirm}>
          Save and Go Back
        </Button>
      </Container>
    </Loading>
  );
}
